export function getToken() {
  return localStorage.getItem("access_token");
}

export function setToken(token: string) {
  localStorage.setItem("access_token", token);
}

export function clearToken() {
  localStorage.removeItem("access_token");
}

// Clear all localStorage data used by the application
// Clear all localStorage data used by the application
export function clearAllStorageData(): void {
  console.log("🧹 Clearing all localStorage data");

  // ✅ Preserve per-device UX flags that should survive logout
  const trialBannerDismissed = localStorage.getItem("trial_banner_dismissed_v2");
  // If later you want to keep goal level too, you can also do:
  // const goalLevel = localStorage.getItem("goal_level");

  // List of all localStorage keys used by the application
  const keysToRemove = [
    // Authentication related
    "access_token",
    "access-token", // Alternative key used in navbar
    "user_data",
    "sb-nluntjftnyzlxhatvvks-auth-token",
    
    // Guest user related
    "guestExamUser",
    "guestToken",
    "guest_id",
    
    // Session and exam related
    "session_id",
    "exam_id",
    "current_exam_id",
    "current_user_id",
    "current_task_attempt_id",
    "current_task_transcript",
    
    // Task retry related
    "task2_retry_use_same_topic",
    
    // Goal level
    "goal_level",
    
    // Analysis data
    "latest_analysis_data",
    "combined_feedback_data",
  ];
  
  // Remove all known keys
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear any remaining localStorage items (comprehensive cleanup)
  localStorage.clear();

  // 🔁 Restore preserved keys so UX flags survive logout
  if (trialBannerDismissed !== null) {
    localStorage.setItem("trial_banner_dismissed_v2", trialBannerDismissed);
  }
  // If you decide to preserve goal_level too, then:
  // if (goalLevel !== null) {
  //   localStorage.setItem("goal_level", goalLevel);
  // }
  
  console.log("✅ All localStorage data cleared");
}


export function getUserIdFromToken(): string | null {
  const token = getToken();
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const data = JSON.parse(json);
    return data.sub || data.user_id || data.id || data.userId || null;
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return null;
  }
}

export function getGuestId(): string | null {
  return localStorage.getItem("guest_id");
}

export function setGuestId(id: string): void {
  localStorage.setItem("guest_id", id);
}

export function clearGuestId(): void {
  localStorage.removeItem("guest_id");
}

export function clearGuestExamUser(): void {
  localStorage.removeItem("guestExamUser");
}

export function getSessionId(): string | null {
  return localStorage.getItem("session_id");
}

export function setSessionId(id: string): void {
  localStorage.setItem("session_id", id);
}

export function clearSessionId(): void {
  localStorage.removeItem("session_id");
}

export function getExamId(): string | null {
  return localStorage.getItem("exam_id");
}

export function setExamId(id: string): void {
  localStorage.setItem("exam_id", id);
}

export function clearExamId(): void {
  localStorage.removeItem("exam_id");
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

export const getGoalLevel = (): string | null => {
  try {
    const goalLevel = localStorage.getItem('goal_level');
    return goalLevel || null;
  } catch (error) {
    console.warn('Goal level not available in localStorage:', error);
    return null; // Always return null on error, never throw
  }
};

// Generate a unique session ID using the same logic as exam ID generation
export function generateSessionId(): string {
  return 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// Check if current user is a guest
export function isGuestUser(): boolean {
  try {
    const guestExamUser = localStorage.getItem("guestExamUser");
    console.log("🔍 isGuestUser - guestExamUser from localStorage:", guestExamUser);
    
    if (!guestExamUser) {
      console.log("🔍 isGuestUser - no guestExamUser found");
      return false;
    }
    
    const parsed = JSON.parse(guestExamUser);
    console.log("🔍 isGuestUser - parsed data:", parsed);
    console.log("🔍 isGuestUser - is_guest value:", parsed.is_guest);
    console.log("🔍 isGuestUser - is_guest === true:", parsed.is_guest === true);
    
    return parsed.is_guest === true;
  } catch (error) {
    console.warn('Error checking guest status:', error);
    return false;
  }
}

// Get guest exam user data
export function getGuestExamUser(): any | null {
  try {
    const guestExamUser = localStorage.getItem("guestExamUser");
    return guestExamUser ? JSON.parse(guestExamUser) : null;
  } catch (error) {
    console.warn('Error getting guest exam user:', error);
    return null;
  }
}

// Get user ID from user_data localStorage (for paid users)
export function getUserDataId(): string | null {
  try {
    const userData = localStorage.getItem("user_data");
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.id || null;
    }
    return null;
  } catch (error) {
    console.warn('Error getting user data id:', error);
    return null;
  }
}


// Get user email from user_data localStorage (for paid users)
export function getUserDataEmail(): string | null {

  try {
    const userData = localStorage.getItem("user_data");
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.email || null;
    }
    return null;
  } catch (error) {
    console.warn("Error getting user data email:", error);
    return null;
  }
}

// Clear retry and exam-related localStorage keys (used when navigating to mic-test or dashboard)
export function clearRetryAndExamStorage(): void {
  try {
    const keys = [
      'current_retry_task_type',
      'current_user_id',
      'current_exam_id',
      'current_task_attempt_id',
      'combined_feedback_data',
      'combined_feedback_exam_id',
      'task2_retry_use_same_topic',
      'guestToken',
      'p2p_last_exam_complete_event_exam_id'
    ];
    keys.forEach(k => localStorage.removeItem(k));
    console.log('🧹 Cleared retry/exam-related localStorage keys');
  } catch (e) {
    console.warn('Failed to clear retry/exam storage:', e);
  }
}

// utils/storage.ts

// Keys we want to keep even when user logs out
const PRESERVED_KEYS = ["trial_banner_dismissed_v2"];

export const clearAllStorageExceptPreserved = () => {
  try {
    // 1) Remember the values we want to keep
    const preservedEntries: Record<string, string | null> = {};

    PRESERVED_KEYS.forEach((key) => {
      preservedEntries[key] = localStorage.getItem(key);
    });

    console.log("🧹 Clearing all localStorage data (preserving banner flag)");

    // 2) Clear everything
    localStorage.clear();

    // 3) Put back only the preserved keys
    PRESERVED_KEYS.forEach((key) => {
      const value = preservedEntries[key];
      if (value !== null && value !== undefined) {
        localStorage.setItem(key, value);
      }
    });

    console.log("✅ LocalStorage cleared, preserved:", PRESERVED_KEYS);
  } catch (err) {
    console.error("Failed to clear localStorage safely", err);
  }
};

